import { useEffect, useState } from 'react'

import IconMenuOpen from '@mui/icons-material/MenuOpen'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import { Theme, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import { hasFeature } from 'Common/environment'
import { useMobileTopNavStore } from 'Common/store/MobileTopNavStore'

import { NewDrawerItem, NewDrawerListItem } from './NewDrawerListItem'

type Props = {
    drawerItems: NewDrawerItem[]
    parentItem?: React.ReactNode
    overviewItem?: React.ReactNode
    color?: string
    fontColor?: string
    drawerWidth?: number
    hasStickyHeader?: boolean
    overviewVariant?: 'default' | 'block'
}

const workLayerSx = (theme: Theme) => ({
    '& .MuiListItem-root': {
        backgroundColor: '#162B29',
        color: '#EAF2EF',
        ...theme.applyStyles('dark', {
            backgroundColor: theme.vars.palette.background.paper,
            backgroundImage: theme.vars.overlays[1],
        }),
    },
    '& .MuiListItem-root .MuiSvgIcon-root': {
        color: '#EAF2EF',
    },
})
export const mobileSubNavClearance = 52

const editionBlockSx = (theme: Theme) => ({
    '& .MuiListItem-root': {
        backgroundColor: '#DDE9E3',
        color: '#2B403C',
        borderBottom: '1px solid rgba(0,0,0,0.12)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
        ...theme.applyStyles('dark', {
            backgroundColor: theme.vars.palette.background.paper,
            backgroundImage: theme.vars.overlays[1],
            color: '#DDE9E3',
            borderBottom: '1px solid rgba(255,255,255,0.12)',
            boxShadow: 'none',
        }),
    },
    '& .MuiListItem-root .MuiSvgIcon-root': {
        color: '#2B403C',
        ...theme.applyStyles('dark', {
            color: '#DDE9E3',
        }),
    },
})

export default function NewSubNavigationMenu(props: Readonly<Props>) {
    const {
        drawerItems,
        parentItem,
        overviewItem,
        color,
        fontColor,
        drawerWidth = 230,
        hasStickyHeader = false,
        overviewVariant = 'default',
    } = props

    const isNewNav = hasFeature('newNavigation')
    const theme = useTheme()
    const isBelowMd = useMediaQuery(theme.breakpoints.down('md'))
    const { showMobileTopNav, setShowMobileTopNav, setHasMobileTopNav } = useMobileTopNavStore()
    const [showMobileNav, setShowMobileNav] = useState(false)

    useEffect(() => {
        setHasMobileTopNav(isBelowMd)
        return isNewNav ? () => setHasMobileTopNav(false) : undefined
    }, [isBelowMd, setHasMobileTopNav, isNewNav])

    const accentColor = isNewNav ? theme.vars.palette.action.selected : color
    const accentFontColor = isNewNav ? theme.vars.palette.text.primary : fontColor

    const headerItems = (
        <>
            {parentItem ?? null}
            {overviewItem ?? null}
        </>
    )

    const renderDrawerItems = (includeDefaultOpen: boolean) =>
        drawerItems.map((item) => (
            <NewDrawerListItem
                key={item.primary}
                primary={item.primary}
                icon={item?.icon}
                secondary={item.secondary}
                link={item.link}
                color={accentColor}
                fontColor={accentFontColor}
                activeContrast={isNewNav}
                defaultOpen={includeDefaultOpen ? item?.defaultOpen : undefined}
            >
                {item?.children}
            </NewDrawerListItem>
        ))

    if (isNewNav) {
        const header = hasStickyHeader ? (
            headerItems
        ) : (
            <>
                {parentItem ? <Box sx={workLayerSx(theme)}>{parentItem}</Box> : null}
                {overviewItem ? (
                    <Box
                        sx={
                            overviewVariant === 'block' ? editionBlockSx(theme) : workLayerSx(theme)
                        }
                    >
                        {overviewItem}
                    </Box>
                ) : null}
            </>
        )
        return (
            <>
                <IconButton
                    color="inherit"
                    aria-label="Open sub menu"
                    onClick={() => setShowMobileNav(true)}
                    size="large"
                    sx={{
                        display: { xs: 'inline-flex', md: 'none' },
                        displayPrint: 'none',
                        position: 'fixed',
                        top: 8,
                        left: 8,
                        zIndex: (theme) => theme.zIndex.drawer + 1,
                    }}
                >
                    <IconMenuOpen fontSize="large" />
                </IconButton>
                <Drawer
                    variant="permanent"
                    anchor="left"
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        displayPrint: 'none',
                        width: drawerWidth,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: drawerWidth,
                            boxSizing: 'border-box',
                            left: 'unset',
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: 'background.default',
                        },
                    }}
                >
                    {hasStickyHeader && header}
                    <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
                        {!hasStickyHeader && header}
                        <List disablePadding>{renderDrawerItems(true)}</List>
                    </Box>
                </Drawer>
                <SwipeableDrawer
                    sx={{
                        display: { xs: 'block', md: 'none', displayPrint: 'none', width: 300 },
                        '& .MuiDrawer-paper': { backgroundColor: 'background.default' },
                    }}
                    open={showMobileNav}
                    onClose={() => setShowMobileNav(false)}
                    onOpen={() => setShowMobileNav(true)}
                >
                    {hasStickyHeader && header}
                    <Box
                        sx={{ overflowY: 'auto', flexGrow: 1 }}
                        onClick={() => setShowMobileNav(false)}
                    >
                        {!hasStickyHeader && header}
                        <List disablePadding>{renderDrawerItems(false)}</List>
                    </Box>
                </SwipeableDrawer>
            </>
        )
    }

    return (
        <>
            <Drawer
                variant="permanent"
                anchor="left"
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    displayPrint: 'none',
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                        left: 'unset',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <Box sx={{ height: (theme) => theme.mixins.toolbar.minHeight, flexShrink: 0 }} />
                {hasStickyHeader && headerItems}
                <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
                    <List>
                        {!hasStickyHeader && headerItems}
                        {renderDrawerItems(true)}
                    </List>
                </Box>
            </Drawer>
            <SwipeableDrawer
                sx={{ display: { xs: 'block', md: 'none', displayPrint: 'none', width: 300 } }}
                open={showMobileTopNav}
                onClose={() => setShowMobileTopNav(false)}
                onOpen={() => setShowMobileTopNav(true)}
            >
                <Box sx={{ height: (theme) => theme.mixins.toolbar.minHeight, flexShrink: 0 }} />
                {hasStickyHeader && headerItems}
                <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
                    <List>
                        {!hasStickyHeader && headerItems}
                        {renderDrawerItems(false)}
                    </List>
                </Box>
            </SwipeableDrawer>
        </>
    )
}
